// testSessionCache.js

const PREFIX = "test-session-";

function getKey(testId) {
  return `${PREFIX}${testId}`;
}

/**
 * Create a new test session in sessionStorage
 */
export function createSession(testId, questionIds) {
  const session = {
    testId,
    questionIds,
    currentIndex: 0,
    loadedQuestions: {},
    startedAt: new Date().toISOString(),
    phase: "checkin",
    roll: null,
    studentInfo: null, // { name, className }
  };
  sessionStorage.setItem(getKey(testId), JSON.stringify(session));
  return session;
}

/**
 * Get the full session object. Returns null if not found.
 */
export function getSession(testId) {
  const raw = sessionStorage.getItem(getKey(testId));
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Check if a session exists for this test
 */
export function hasSession(testId) {
  return sessionStorage.getItem(getKey(testId)) !== null;
}

/**
 * Save the entire session object back
 */
function saveSession(testId, session) {
  sessionStorage.setItem(getKey(testId), JSON.stringify(session));
}

/**
 * Get the list of question IDs
 */
export function getQuestionIds(testId) {
  const session = getSession(testId);
  return session?.questionIds ?? [];
}

/**
 * Get current question index
 */
export function getCurrentIndex(testId) {
  const session = getSession(testId);
  return session?.currentIndex ?? 0;
}

/**
 * Set current question index
 */
export function setCurrentIndex(testId, index) {
  const session = getSession(testId);
  if (!session) return;
  session.currentIndex = index;
  saveSession(testId, session);
}

/**
 * Get a loaded question by its ID. Returns null if not cached.
 */
export function getLoadedQuestion(testId, questionId) {
  const session = getSession(testId);
  return session?.loadedQuestions?.[questionId] ?? null;
}

/**
 * Cache a loaded question
 */
export function setLoadedQuestion(testId, questionId, questionData) {
  const session = getSession(testId);
  if (!session) return;
  session.loadedQuestions[questionId] = questionData;
  saveSession(testId, session);
}

/**
 * Check if a question is already cached
 */
export function isQuestionLoaded(testId, questionId) {
  const session = getSession(testId);
  return !!session?.loadedQuestions?.[questionId];
}

/**
 * Get total question count
 */
export function getQuestionCount(testId) {
  const session = getSession(testId);
  return session?.questionIds?.length ?? 0;
}

/**
 * Clear session for a test
 */
export function clearSession(testId) {
  sessionStorage.removeItem(getKey(testId));
}

/**
 * Clear all test sessions
 */
export function clearAllSessions() {
  const keys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key.startsWith(PREFIX)) keys.push(key);
  }
  keys.forEach(k => sessionStorage.removeItem(k));
}

// ── Phase ──

export function getPhase(testId) {
  const session = getSession(testId);
  return session?.phase ?? "checkin";
}

export function setPhase(testId, phase) {
  const session = getSession(testId);
  if (!session) return;
  session.phase = phase;
  saveSession(testId, session);
}

// ── Roll (unique student identifier) ──

export function getRoll(testId) {
  const session = getSession(testId);
  return session?.roll ?? null;
}

export function setRoll(testId, roll) {
  const session = getSession(testId);
  if (!session) return;
  session.roll = roll;
  saveSession(testId, session);
}

// ── Student Info ({ name, className }) ──

export function getStudentInfo(testId) {
  const session = getSession(testId);
  return session?.studentInfo ?? null;
}

export function setStudentInfo(testId, info) {
  const session = getSession(testId);
  if (!session) return;
  session.studentInfo = info;
  saveSession(testId, session);
}